import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import Stack from 'src/components/widgets/stack/stack';
import { AiOutlineButton, AiPrimaryButton } from 'src/components/widgets/ai';
import { GhostButton } from 'src/components/widgets/buttons';
import { T } from 'src/shared/helpers';
import {
    QuestionCard,
    QuestionBody,
    QuestionHeading,
    QuestionHint,
    QuestionChoices,
    QuestionChoice,
    QuestionFreeText,
    QuestionFooter,
    QuestionActions
} from 'src/components/shared/aiAssistant/questionPrimitives';
import { AgentAnsweredQuestion, AgentQuestionAnswer, AgentQuestionnaire, AgentQuestionnaireSubmission } from './types';
import { isQuestionAnswered } from './agentInteractionState';

const ErrorText = styled.span`
    color: #b3261e;
    font-size: 12px;
`;

// Answers never mention AI credits: the agent sometimes appends "(uses AI credits)" or a sentence about them, which is dropped here.
const CREDIT_PARENTHETICAL = /\s*[\[(][^)\]]*\bcredit[io]?s?\b[^)\]]*[\])]/gi;
const CREDIT_SENTENCE = /[;,]?[^.!?;]*credit[io]?s?[^.!?;]*[.!?]?/gi;
const CREDIT_SUFFIX = /\s*[-–—·:]\s*[^-–—·:]*\bcredit[io]?s?\b.*$/i;
const withoutCreditMentions = (text: string) =>
    text
        .replace(CREDIT_PARENTHETICAL, '')
        .replace(CREDIT_SUFFIX, '')
        .replace(CREDIT_SENTENCE, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

interface Props {
    conversationId: number;
    questionnaire: AgentQuestionnaire;
    disabled: boolean;
    initialAnswers?: AgentAnsweredQuestion[];
    correctionAfterTurnId?: number;
    onSubmit: (submission: AgentQuestionnaireSubmission) => Promise<boolean | undefined>;
    onCancel?: () => void;
}

export const AgentQuestionWizard: FC<Props> = ({ conversationId, questionnaire, disabled, initialAnswers, correctionAfterTurnId, onSubmit, onCancel }) => {
    const storageKey = `zakeke.agent.questions.${conversationId}.${questionnaire.turnId}.${correctionAfterTurnId ?? 'new'}`;
    const [answers, setAnswers] = useState<AgentQuestionAnswer[]>(() => {
        try {
            const stored = JSON.parse(window.sessionStorage.getItem(storageKey) ?? 'null');
            if (
                Array.isArray(stored) &&
                stored.length === questionnaire.questions.length &&
                stored.every(
                    (a, i) =>
                        a?.questionId === questionnaire.questions[i].id &&
                        Array.isArray(a.optionIds) &&
                        a.optionIds.every((id: unknown) => typeof id === 'string') &&
                        (a.text === null || typeof a.text === 'string') &&
                        typeof a.skipped === 'boolean'
                )
            )
                return stored;
        } catch {
            /* Storage is optional; answering must still work without it. */
        }
        return questionnaire.questions.map((question) => {
            const previous = initialAnswers?.find((a) => a.questionId === question.id);
            return previous
                ? { questionId: previous.questionId, optionIds: previous.optionIds, text: previous.text, skipped: previous.skipped }
                : { questionId: question.id, optionIds: [], text: question.type === 'text' ? '' : null, skipped: false };
        });
    });
    const [index, setIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const question = questionnaire.questions[index];
    const answer = answers[index];
    const locked = disabled || submitting;
    const allAnswered = questionnaire.questions.every((q, i) => isQuestionAnswered(q, answers[i]));
    useEffect(() => {
        try {
            window.sessionStorage.setItem(storageKey, JSON.stringify(answers));
        } catch {
            /* Optional. */
        }
    }, [storageKey, answers]);

    const update = (next: Partial<AgentQuestionAnswer>) => {
        setError(null);
        setAnswers((current) => current.map((a, i) => (i === index ? { ...a, ...next } : a)));
    };
    const submit = async () => {
        if (locked || !allAnswered) return;
        setSubmitting(true);
        setError(null);
        try {
            if (await onSubmit({ turnId: questionnaire.turnId, answers, correctionAfterTurnId })) {
                try {
                    window.sessionStorage.removeItem(storageKey);
                } catch {
                    /* Optional. */
                }
            } else setError(T._('Your answers were not sent. They are still here; please try again.', 'Admin'));
        } catch {
            setError(T._('Your answers were not sent. Please try again.', 'Admin'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <QuestionCard $gap='0' aria-label={T._('Clarification questions', 'Admin')}>
            <QuestionBody $gap='10px'>
                {(correctionAfterTurnId || question.title.trim() !== question.question.trim()) && (
                    <QuestionHeading>{correctionAfterTurnId ? T._('Update your answers', 'Admin') : question.title}</QuestionHeading>
                )}
                <QuestionChoices disabled={locked}>
                    <legend>{question.question}</legend>
                    <Stack $gap='7px'>
                        {question.options.map((option) => (
                            <QuestionChoice key={option.id} $selected={answer.optionIds.includes(option.id)}>
                                <input
                                    type={question.type === 'multiple' ? 'checkbox' : 'radio'}
                                    name={`question-${questionnaire.turnId}-${question.id}`}
                                    checked={answer.optionIds.includes(option.id)}
                                    onChange={() =>
                                        update({
                                            optionIds:
                                                question.type === 'multiple'
                                                    ? answer.optionIds.includes(option.id)
                                                        ? answer.optionIds.filter((id) => id !== option.id)
                                                        : [...answer.optionIds, option.id]
                                                    : [option.id],
                                            text: null,
                                            skipped: false
                                        })
                                    }
                                />
                                <span>
                                    <strong>{withoutCreditMentions(option.label) || option.label}</strong>
                                    {option.description && withoutCreditMentions(option.description) && (
                                        <small>{withoutCreditMentions(option.description)}</small>
                                    )}
                                </span>
                            </QuestionChoice>
                        ))}
                        {question.allowFreeText && question.type !== 'text' && (
                            <QuestionChoice $selected={answer.text !== null}>
                                <input
                                    type='radio'
                                    name={`question-${questionnaire.turnId}-${question.id}-custom`}
                                    checked={answer.text !== null}
                                    onChange={() => update({ optionIds: [], text: answer.text ?? '', skipped: false })}
                                />
                                <strong>{T._('Write a different answer', 'Admin')}</strong>
                            </QuestionChoice>
                        )}
                        {question.allowFreeText && (question.type === 'text' || answer.text !== null) && (
                            <QuestionFreeText
                                aria-label={T._('Your answer', 'Admin')}
                                placeholder={T._('Write your answer…', 'Admin')}
                                value={answer.text ?? ''}
                                maxLength={4000}
                                onChange={(event) => update({ optionIds: [], text: event.target.value, skipped: false })}
                            />
                        )}
                    </Stack>
                </QuestionChoices>
                {!question.required && (
                    <GhostButton
                        type='button'
                        $size='small'
                        disabled={locked}
                        onClick={() => {
                            update({ optionIds: [], text: null, skipped: true });
                            if (index < questionnaire.questions.length - 1) setIndex(index + 1);
                        }}
                    >
                        {answer.skipped ? T._('Question skipped', 'Admin') : T._('Skip this question', 'Admin')}
                    </GhostButton>
                )}
                {error && <ErrorText role='alert'>{error}</ErrorText>}
            </QuestionBody>
            <QuestionFooter $horizontal $gap='8px'>
                <QuestionActions $horizontal $gap='8px'>
                    {questionnaire.questions.length > 1 && (
                        <>
                            <AiOutlineButton type='button' $size='small' disabled={locked || index === 0} onClick={() => setIndex(index - 1)}>
                                {T._('Back', 'Admin')}
                            </AiOutlineButton>
                            <QuestionHint>
                                {index + 1} / {questionnaire.questions.length}
                            </QuestionHint>
                        </>
                    )}
                    {onCancel && (
                        <GhostButton type='button' $size='small' disabled={locked} onClick={onCancel}>
                            {T._('Cancel', 'Admin')}
                        </GhostButton>
                    )}
                </QuestionActions>
                {index < questionnaire.questions.length - 1 ? (
                    <AiPrimaryButton type='button' $size='small' disabled={locked || !isQuestionAnswered(question, answer)} onClick={() => setIndex(index + 1)}>
                        {T._('Next', 'Admin')}
                    </AiPrimaryButton>
                ) : (
                    <AiPrimaryButton type='button' $size='small' disabled={locked || !allAnswered} onClick={() => void submit()}>
                        {T._('Send answers', 'Admin')}
                    </AiPrimaryButton>
                )}
            </QuestionFooter>
        </QuestionCard>
    );
};
